/**
 * Checkout page — everything on one screen.
 *
 * Name, email, pickup time, tip, and Stripe card form all visible at once.
 * Uses Stripe's deferred intent flow — card form renders without a clientSecret.
 * Order is created on the backend only when the user clicks Pay.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements } from '@stripe/react-stripe-js'
import { ArrowLeft, ChevronDown, Loader2, Mail, MapPin } from 'lucide-react'
import { Button, Input, Label, Separator, ThemeSwitcher } from '@/components/ui'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCustomerInfo, setPickupInfo, setPaymentMethod } from '@/store/orderSlice'
import { selectCartItems, selectCartTotals } from '@/store/cartSlice'
import { getEnvironmentConfig } from '@/config/environment'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { restaurantConfig } from '@/config/restaurant'
import { formatPrice } from '@/utils/formatting'
import { sanitizeInput, sanitizeEmail, isValidEmail } from '@/utils/validation'
import { loadFromStorage, saveToStorage, STORAGE_KEYS } from '@/utils/storage'
import { useCheckoutFlow, type CheckoutResult } from '@/hooks/useCheckoutFlow'
import { useEmailVerification } from '@/hooks/useEmailVerification'
import {
  PickupTimePicker,
  StripePaymentForm,
  OrderSuccessCard,
  TipSelector,
  OrderSummaryPanel,
} from '@/components/checkout'
import type { VerifiedContactData } from '@/components/checkout/ContactVerification'

interface CheckoutPageProps {
  navigate: (path: string) => void
}

export function CheckoutPage({ navigate }: CheckoutPageProps) {
  const dispatch = useAppDispatch()
  const order = useAppSelector((s) => s.order)
  const items = useAppSelector(selectCartItems)
  const totals = useAppSelector(selectCartTotals)
  const config = getEnvironmentConfig()
  const { data: menuConfig } = menuWidgetApi.menuConfig.get({ restaurant_id: config.restaurantId })

  const firstMenuId = menuConfig?.menus?.[0]?.id
  const { data: orderConfig } = menuWidgetApi.menuOrderConfig.get(
    firstMenuId ? { id: firstMenuId } : undefined,
    { skip: !firstMenuId }
  )

  const stripePublishableKey = orderConfig?.stripe_publishable_key || import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || ''
  const stripePromise = useMemo(
    () => stripePublishableKey ? loadStripe(stripePublishableKey) : null,
    [stripePublishableKey]
  )
  const useStripePayment = !!stripePublishableKey

  const {
    step: checkoutStep,
    error: checkoutError,
    orderResult,
    createOrder,
    finalizeOrder,
    processCheckout,
  } = useCheckoutFlow()

  const isCreatingOrder = checkoutStep === 'authenticating' || checkoutStep === 'syncing-cart' || checkoutStep === 'validating' || checkoutStep === 'processing'

  // UI state
  const [orderNumber, setOrderNumber] = useState<string | null>(null)
  const [eta, setEta] = useState<string | null>(null)
  const [showOrderSummary, setShowOrderSummary] = useState(false)
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)

  // Tip state
  const [tipPercent, setTipPercent] = useState<number | 'custom'>(0)
  const [customTipInput, setCustomTipInput] = useState('')

  // Tip in DOLLARS (cart prices are in dollars, not cents)
  const tipAmount = useMemo(() => {
    if (tipPercent === 'custom') {
      const dollars = parseFloat(customTipInput || '0')
      return isNaN(dollars) || dollars < 0 ? 0 : Math.round(Math.min(dollars, 999) * 100) / 100
    }
    return Math.round(totals.subtotal * tipPercent) / 100
  }, [tipPercent, customTipInput, totals.subtotal])

  // Form state
  const [firstName, setFirstName] = useState(() => {
    if (order.customerInfo?.firstName) return order.customerInfo.firstName
    const saved = loadFromStorage<{ name: string }>(STORAGE_KEYS.CUSTOMER_INFO)
    return saved?.name || ''
  })
  const [emailInput, setEmailInput] = useState(() => {
    const saved = loadFromStorage<{ email: string }>(STORAGE_KEYS.CUSTOMER_INFO)
    return saved?.email || ''
  })
  const [contactData, setContactData] = useState<VerifiedContactData | null>(null)
  const isContactVerified = !!contactData?.contactVerified

  // Email verification (for persistence check)
  const emailAuth = useEmailVerification()
  const isEmailVerified = emailAuth.state === 'verified' || isContactVerified

  // Auto-verify from persistence on mount
  useEffect(() => {
    if (emailAuth.persistedEmail && emailAuth.state === 'verified') {
      setEmailInput(emailAuth.persistedEmail)
      setContactData({
        phone: '',
        email: emailAuth.persistedEmail,
        notificationChannel: 'email',
        contactVerified: true,
      })
    }
  }, [emailAuth.persistedEmail, emailAuth.state])

  // Persist customer info
  useEffect(() => {
    if (firstName || emailInput) {
      saveToStorage(STORAGE_KEYS.CUSTOMER_INFO, { name: firstName, email: emailInput })
    }
  }, [firstName, emailInput])

  const hasValidEmail = isValidEmail(emailInput.trim())

  const restaurantName = menuConfig?.title || restaurantConfig.name
  const { street, city, state: addrState } = restaurantConfig.address

  // Prevents empty cart guard from redirecting after cart is cleared but before orderNumber is set
  const checkoutInProgress = useRef(false)
  // Safety ref for orderResult access in handleStripeSuccess
  const latestOrderResult = useRef<CheckoutResult | null>(null)

  // Empty cart guard
  useEffect(() => {
    if (items.length === 0 && !orderNumber && !checkoutInProgress.current) {
      navigate('/menu')
    }
  }, [items.length, orderNumber, navigate])

  // Called by StripePaymentForm when user clicks Pay
  const handleCreateOrder = async () => {
    checkoutInProgress.current = true

    const safeName = sanitizeInput(firstName)
    const safeEmail = sanitizeEmail(emailInput.trim())

    dispatch(setCustomerInfo({ firstName: safeName, lastName: '', email: safeEmail, phone: '' }))
    dispatch(setPaymentMethod('card-online'))

    const result = await createOrder({
      customerName: safeName || 'Guest',
      customerEmail: safeEmail,
      customerPhone: '',
      notificationChannel: safeEmail ? 'email' : '',
      contactVerified: isEmailVerified,
      scheduledPickupAt: order.pickupInfo?.pickupTime === 'ASAP' ? null : order.pickupInfo?.pickupTime || null,
      tipAmount: Math.round(tipAmount * 100), // convert dollars to cents for backend
      orderType: 'pickup',
      menuId: firstMenuId || undefined,
      notes: order.specialInstructions || '',
    })

    if (!result) {
      checkoutInProgress.current = false
      return null
    }

    latestOrderResult.current = result
    return { clientSecret: result.clientSecret, orderId: result.orderId }
  }

  const handleStripeSuccess = async () => {
    const result = orderResult || latestOrderResult.current
    if (result) {
      await finalizeOrder()  // await before showing success
      setOrderNumber(result.orderNumber)
      setEta(result.estimatedReadyAt || new Date(Date.now() + 30 * 60 * 1000).toISOString())
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleStripeError = (_msg: string) => {
    // StripePaymentForm handles its own error display
  }

  // Non-Stripe fallback (mock/cash)
  const handleMockPay = async () => {
    if (isCreatingOrder) return
    checkoutInProgress.current = true

    const safeName = sanitizeInput(firstName)
    const safeEmail = sanitizeEmail(emailInput.trim())

    dispatch(setCustomerInfo({ firstName: safeName, lastName: '', email: safeEmail, phone: '' }))
    dispatch(setPaymentMethod('card-online'))

    const result = await processCheckout({
      customerName: safeName || 'Guest',
      customerEmail: safeEmail,
      customerPhone: '',
      notificationChannel: safeEmail ? 'email' : '',
      contactVerified: isEmailVerified,
      scheduledPickupAt: order.pickupInfo?.pickupTime === 'ASAP' ? null : order.pickupInfo?.pickupTime || null,
      tipAmount: Math.round(tipAmount * 100), // convert dollars to cents for backend
      orderType: 'pickup',
      menuId: firstMenuId || undefined,
      notes: order.specialInstructions || '',
    })

    if (result) {
      setOrderNumber(result.orderNumber)
      setEta(result.estimatedReadyAt || new Date(Date.now() + 30 * 60 * 1000).toISOString())
    } else {
      checkoutInProgress.current = false
    }
  }

  // --- Computed values and hooks that MUST be before any early returns ---
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0)
  const currency = items[0]?.menuItem?.currency || orderConfig?.currency || 'CAD'
  const taxRate = orderConfig?.tax_rate ? Number(orderConfig.tax_rate) : null

  const grandTotal =
    totals.subtotal
    + (taxRate ? Math.round(totals.subtotal * taxRate * 100) / 100 : 0)
    + tipAmount

  // Stripe requires amounts in cents
  const grandTotalCents = Math.round(grandTotal * 100)

  // Stripe Elements options — deferred intent flow (no clientSecret needed)
  const stripeOptions = useMemo(() => ({
    mode: 'payment' as const,
    amount: Math.max(grandTotalCents, 50), // Stripe minimum 50 cents
    currency: currency.toLowerCase(),
    appearance: {
      theme: 'stripe' as const,
      variables: { borderRadius: '8px' },
    },
  }), [grandTotalCents, currency])

  // Success screen — AFTER all hooks
  if (orderNumber) {
    const result = orderResult || latestOrderResult.current
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-2xl">
          <OrderSuccessCard
            orderNumber={orderNumber}
            estimatedTime={eta}
            trackingCode={result?.trackingCode || order.trackingCode || null}
            onBackToStore={() => navigate('/menu')}
            onTrackOrder={() => navigate('/tracking')}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 pb-24 lg:pb-6 max-w-6xl">
        {/* Header */}
        <header className="mb-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">{restaurantName}</h1>
            <div className="flex items-center gap-1">
              <ThemeSwitcher />
              <Button variant="ghost" onClick={() => navigate('/menu')} className="text-sm text-muted-foreground">
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to menu
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1.5">
            <MapPin className="h-3.5 w-3.5 shrink-0" />
            Pickup at {street}, {city}, {addrState}
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form column */}
          <div className="lg:col-span-3 space-y-8">
            {/* Customer info */}
            <div className="space-y-3">
              <h2 className="text-lg font-semibold">Your information</h2>
              <div>
                <Label htmlFor="ck-name" className="text-sm font-medium">Name</Label>
                <Input
                  id="ck-name"
                  placeholder="Optional"
                  value={firstName}
                  onChange={(e) => setFirstName(sanitizeInput(e.target.value).slice(0, 50))}
                  maxLength={50}
                  autoComplete="given-name"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="ck-email" className="text-sm font-medium">Email address</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    id="ck-email"
                    type="email"
                    placeholder="you@example.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(sanitizeEmail(e.target.value))}
                    maxLength={254}
                    autoComplete="email"
                  />
                  {isEmailVerified && (
                    <span className="flex items-center gap-1 text-xs font-medium text-green-600 shrink-0">
                      <Mail className="h-3.5 w-3.5" />
                      Verified
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Pickup time */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold">Pickup time</h3>
              <PickupTimePicker
                value={order.pickupInfo?.pickupTime || ''}
                onChange={(time) => {
                  dispatch(setPickupInfo({
                    ...(order.pickupInfo || { pickupTime: '' }),
                    pickupTime: time,
                  }))
                }}
              />
            </div>

            <Separator />

            {/* Tip */}
            <TipSelector
              subtotal={totals.subtotal}
              tipPercent={tipPercent}
              customTipInput={customTipInput}
              onTipPercentChange={setTipPercent}
              onCustomTipInputChange={setCustomTipInput}
              tipAmount={tipAmount}
              currency={currency}
            />

            <Separator />

            {/* Error message */}
            {checkoutError && (
              <p className="text-sm text-destructive">{checkoutError}</p>
            )}

            {/* Payment — Stripe card form always visible */}
            {useStripePayment && stripePromise ? (
              <Elements stripe={stripePromise} options={stripeOptions}>
                <StripePaymentForm
                  onCreateOrder={handleCreateOrder}
                  onSuccess={handleStripeSuccess}
                  onError={handleStripeError}
                  isProcessing={isPaymentProcessing}
                  setIsProcessing={setIsPaymentProcessing}
                  total={grandTotalCents}
                  currency={currency}
                  disabled={!hasValidEmail}
                  isCreatingOrder={isCreatingOrder}
                  checkoutStep={checkoutStep}
                />
              </Elements>
            ) : useStripePayment ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-muted-foreground">Loading payment...</span>
              </div>
            ) : (
              /* Non-Stripe fallback */
              <Button
                variant="black"
                size="lg"
                onClick={handleMockPay}
                disabled={isCreatingOrder || !hasValidEmail}
                className="w-full"
              >
                {isCreatingOrder ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Place Order · ${formatPrice(grandTotal, currency)}`
                )}
              </Button>
            )}
          </div>

          {/* Desktop sidebar */}
          <aside className="hidden lg:block lg:col-span-2">
            <div className="bg-muted/30 border rounded-xl p-6 space-y-4 sticky top-6">
              <OrderSummaryPanel
                items={items}
                subtotal={totals.subtotal}
                totalItems={totalItems}
                taxRate={taxRate}
                deliveryFee={null}
                tipAmount={tipAmount}
                orderType="pickup"
                grandTotal={grandTotal}
                currency={currency}
              />
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile sticky bottom bar */}
      {showOrderSummary && (
        <div
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={() => setShowOrderSummary(false)}
        />
      )}
      <div className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
        {showOrderSummary && (
          <div className="bg-card border-t shadow-lg max-h-[50vh] overflow-y-auto px-4 py-4 space-y-4">
            <OrderSummaryPanel
              items={items}
              subtotal={totals.subtotal}
              totalItems={totalItems}
              taxRate={taxRate}
              deliveryFee={null}
              tipAmount={tipAmount}
              orderType="pickup"
              grandTotal={grandTotal}
              currency={currency}
            />
          </div>
        )}
        <button
          onClick={() => setShowOrderSummary(!showOrderSummary)}
          className="w-full bg-card border-t px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]"
        >
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-medium">
              <ChevronDown className={`h-4 w-4 transition-transform ${showOrderSummary ? 'rotate-180' : ''}`} />
              Order summary &middot; {totalItems} {totalItems === 1 ? 'item' : 'items'}
            </span>
            <span className="font-bold">{formatPrice(grandTotal, currency)}</span>
          </div>
        </button>
      </div>
    </div>
  )
}
